import { HttpErrorResponse } from '@angular/common/http';
import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity';
import { createFeature, createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';

import { OrganizationChecklistActions } from '../actions/checklist.actions';
import { ORGANIZATION_CHECKLIST_FEATURE_KEY } from '../constants/constants';
import { GetOrganizationChecklistDto } from '../interfaces/checklists';

export interface OrganizationChecklistState extends EntityState<GetOrganizationChecklistDto> {
    loading: boolean;
    error: HttpErrorResponse | null;
}

export const organizationChecklistAdapter: EntityAdapter<GetOrganizationChecklistDto> =
    createEntityAdapter<GetOrganizationChecklistDto>();

const initialState: OrganizationChecklistState = organizationChecklistAdapter.getInitialState({
    loading: false,
    error: null,
});

export const selectOrganizationChecklistState = createFeatureSelector<OrganizationChecklistState>(
    ORGANIZATION_CHECKLIST_FEATURE_KEY,
);

const { selectEntities, selectAll } = organizationChecklistAdapter.getSelectors();

export const selectOrganizationChecklistEntities = createSelector(selectOrganizationChecklistState, selectEntities);
export const selectAllOrganizationChecklists = createSelector(selectOrganizationChecklistState, selectAll);

export const selectOrganizationChecklistError = createSelector(
    selectOrganizationChecklistState,
    (state: OrganizationChecklistState) => state.error,
);
export const selectOrganizationChecklistLoadingState = createSelector(
    selectOrganizationChecklistState,
    (state: OrganizationChecklistState) => state.loading,
);

export const selectChecklistById = (id: string) =>
    createSelector(selectOrganizationChecklistEntities, (entities) => entities[id]);

export const organizationChecklistReducer = createReducer(
    initialState,
    on(OrganizationChecklistActions.loadOrganizationChecklists, (state) => ({ ...state, loading: true })),
    on(OrganizationChecklistActions.loadOrganizationChecklistsSuccess, (state, { checklists }) =>
        organizationChecklistAdapter.setAll(checklists, { ...state, loading: false }),
    ),
    on(OrganizationChecklistActions.loadOrganizationChecklistsFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error,
    })),
    on(OrganizationChecklistActions.createChecklist, (state) => ({ ...state, loading: true })),
    on(OrganizationChecklistActions.createChecklistSuccess, (state, { checklist }) =>
        organizationChecklistAdapter.addOne(checklist, { ...state, loading: false }),
    ),
    on(OrganizationChecklistActions.createChecklistFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error,
    })),
    on(OrganizationChecklistActions.updateChecklist, (state) => ({ ...state, loading: true })),
    on(OrganizationChecklistActions.updateChecklistSuccess, (state, { checklist }) =>
        organizationChecklistAdapter.updateOne({ id: checklist.id, changes: checklist }, { ...state, loading: false }),
    ),
    on(OrganizationChecklistActions.updateChecklistFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error,
    })),
    on(OrganizationChecklistActions.loadChecklistById, (state) => ({ ...state, loading: true })),
    on(OrganizationChecklistActions.loadChecklistByIdSuccess, (state, { checklist }) =>
        organizationChecklistAdapter.upsertOne(checklist, { ...state, loading: false }),
    ),
    on(OrganizationChecklistActions.loadChecklistByIdFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error,
    })),

    on(OrganizationChecklistActions.deleteChecklists, (state) => ({ ...state, loading: true })),
    on(OrganizationChecklistActions.deleteChecklistsSuccess, (state, { ids }) =>
        organizationChecklistAdapter.removeMany(ids, { ...state, loading: false }),
    ),
    on(OrganizationChecklistActions.deleteChecklistsFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error,
    })),
);

export const organizationChecklistFeature = createFeature({
    name: ORGANIZATION_CHECKLIST_FEATURE_KEY,
    reducer: organizationChecklistReducer,
});
